import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import * as SelectionActions from 'actions/SelectionActions';
// @ts-expect-error
import SelectionStore from 'notebook/mediocre/selection/SelectionStore';
// @ts-expect-error
import HistoryManager from 'notebook/store/HistoryManager';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

interface DocumentManagerComponentProps<TItem> {
  currentVersion: TItem;
  renderWith: (args: {
    metadataForRestore: {selection: SelectionRestoreData} | null;
    restoreState: (
      e: ElementKeyboardEvent,
      selectionRestoreData: {selection: SelectionRestoreData}
    ) => boolean;
    undoState: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
    redoState: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
    storeState: (selectionData: {selection: SelectionRestoreData}) => void;
    undoStoreState: () => void;
    requestSelection: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
  }) => JSX.Element;
  restoreStateAndSelection: () => void;
  historyManager: typeof HistoryManager;
}

interface State {
  selectionKey: string;
}

interface KeyData {
  key: string;
  data: {selection: SelectionRestoreData};
}

// Composable component that manages the history and selection of a document.

class DocumentManagerComponent<TItem> extends React.Component<DocumentManagerComponentProps<TItem>, State> {
  static propTypes = {
    // The current document to be stored in the HistoryManager (on undo)
    // eslint-disable-next-line react/forbid-prop-types
    currentVersion: PropTypes.any.isRequired,

    // Returns a rendered React component, exposing metadataForRestore, storeState, restoreState,
    // and requestSelection.
    renderWith: PropTypes.func.isRequired,

    // Restores the state to passed-in document and selection.
    restoreStateAndSelection: PropTypes.func.isRequired,

    historyManager: PropTypes.instanceOf(HistoryManager),
  };

  static keyData(key: string, data: {selection: SelectionRestoreData}): KeyData {
    return {key, data};
  }
  static unkeyData(key: string, restoreData?: KeyData): {selection: SelectionRestoreData} | null {
    if ((restoreData != null ? restoreData.key : undefined) !== key) {
      return null;
    }
    return (restoreData as KeyData).data;
  }

  override state: State = {
    selectionKey: _.uniqueId('DocumentManagerComponent'),
  };

  _keyData = (data: {selection: SelectionRestoreData}): KeyData =>
    DocumentManagerComponent.keyData(this.state.selectionKey, data);

  _unkeyData = (data: KeyData): {selection: SelectionRestoreData} | null =>
    DocumentManagerComponent.unkeyData(this.state.selectionKey, data);

  override render(): JSX.Element {
    return (
      <SelectionManagerComponent
        keyData={this._keyData}
        unkeyData={this._unkeyData}
        renderWith={({
          metadataForRestore,
          requestSelection,
        }: {
          metadataForRestore: {selection: SelectionRestoreData} | null;
          requestSelection: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
        }) => (
          <HistoryManagerComponent
            keyData={this._keyData}
            currentVersion={this.props.currentVersion}
            restoreStateAndSelection={this.props.restoreStateAndSelection}
            historyManager={this.props.historyManager}
            renderWith={({
              storeState,
              restoreState,
              undoStoreState,
              undoState,
              redoState,
            }: {
              storeState: (selectionData: {selection: SelectionRestoreData}) => void;
              restoreState: (
                e: ElementKeyboardEvent,
                selectionData: {selection: SelectionRestoreData}
              ) => boolean;
              undoState: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
              redoState: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
              undoStoreState: () => void;
            }) =>
              this.props.renderWith({
                metadataForRestore,
                storeState,
                restoreState,
                undoStoreState,
                requestSelection,
                undoState,
                redoState,
              })
            }
          />
        )}
      />
    );
  }
}

interface SelectionManagerComponentProps {
  keyData: (data: {selection: SelectionRestoreData}) => KeyData;
  unkeyData: (data: KeyData) => {selection: SelectionRestoreData} | null;
  renderWith: (args: {
    metadataForRestore: {selection: SelectionRestoreData} | null;
    requestSelection: (data: {selection: SelectionRestoreData}) => void;
  }) => JSX.Element;
}

interface SelectionManagerComponentState {
  metadataForRestore: {selection: SelectionRestoreData} | null;
}

class SelectionManagerComponent extends React.Component<
  SelectionManagerComponentProps,
  SelectionManagerComponentState
> {
  static propTypes = {
    // Keys a selection object to identify if this component owns the restoreData from the SelectionStore.
    keyData: PropTypes.func.isRequired,

    // Inverse operation of keyData
    unkeyData: PropTypes.func.isRequired,

    // Renders a React component, exposing metadataForRestore and requestSelection.
    renderWith: PropTypes.func.isRequired,
  };

  override state: SelectionManagerComponentState = {metadataForRestore: this._selectionStoreData()};

  _selectionStoreData(): {selection: SelectionRestoreData} | null {
    return this.props.unkeyData(SelectionStore.getState().restoreData);
  }

  selectionChangeHandler = (): void => {
    const metadataForRestore = this._selectionStoreData();
    if (metadataForRestore !== this.state.metadataForRestore) {
      this.setState({metadataForRestore});
    }
  };

  requestSelection = (data: {selection: SelectionRestoreData}): void => {
    SelectionActions.setSelection(this.props.keyData(data));
  };

  override componentWillUnmount(): void {
    SelectionStore.removeChangeListener(this.selectionChangeHandler);
  }

  override componentDidMount(): void {
    SelectionStore.addChangeListener(this.selectionChangeHandler);
  }

  override render(): JSX.Element {
    return this.props.renderWith({
      metadataForRestore: this.state.metadataForRestore,
      requestSelection: this.requestSelection,
    });
  }
}

interface HistoryManagerComponentProps<TItem> {
  historyManager: typeof HistoryManager;
  keyData: (data: {selection: SelectionRestoreData}) => KeyData;
  currentVersion: TItem;
  renderWith: (args: {
    storeState: (selectionData: {selection: SelectionRestoreData}) => void;
    restoreState: (e: ElementKeyboardEvent, selectionData: {selection: SelectionRestoreData}) => boolean;
    undoStoreState: () => void;
    undoState: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
    redoState: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
  }) => JSX.Element;
  restoreStateAndSelection: (state: TItem, selectionData: {selection: SelectionRestoreData}) => void;
}

export class HistoryManagerComponent<TItem> extends React.Component<HistoryManagerComponentProps<TItem>> {
  static propTypes = {
    historyManager: PropTypes.instanceOf(HistoryManager),

    // Keys a selection with the given function so that on restore,
    // the correct component receives the selection.
    keyData: PropTypes.func.isRequired,

    // The current document to be stored in the HistoryManager (on undo)
    // eslint-disable-next-line react/forbid-prop-types
    currentVersion: PropTypes.any.isRequired,

    // Renders a React component, exposing storeState and restoreState
    renderWith: PropTypes.func.isRequired,
  };

  storeState = (selectionData: {selection: SelectionRestoreData}): void => {
    // Passed to editable components, to be called before any change is made. Adds new history.
    this.props.historyManager.pushState({
      state: this.props.currentVersion,
      selectionData: this.props.keyData(selectionData),
    });
  };

  undoStoreState = (): void => {
    // Passed to editable components, to be called in the case that a change is decided to not be made even
    // after calling `storeState`.
    this.props.historyManager.undoPushStateIf(
      (historyState: {state: TItem}) => historyState.state === this.props.currentVersion
    );
  };

  undoState = (selectionData: {selection: SelectionRestoreData}): void => {
    const {currentVersion, historyManager, keyData, restoreStateAndSelection} = this.props;
    if (historyManager.canUndo()) {
      const newState = historyManager.undo({
        state: currentVersion,
        selectionData: keyData(selectionData),
      });
      let state;
      ({state, selectionData} = newState);
      restoreStateAndSelection(state, selectionData);
    }
  };

  redoState = (selectionData: {selection: SelectionRestoreData}): void => {
    const {historyManager, restoreStateAndSelection} = this.props;
    if (historyManager.canRedo()) {
      const newState = historyManager.redo();
      let state;
      ({state, selectionData} = newState);
      restoreStateAndSelection(state, selectionData);
    }
  };

  // Function to handle undo/redo keyboard actions
  restoreState = (e: ElementKeyboardEvent, selectionData: {selection: SelectionRestoreData}): boolean => {
    // Passed to editable components, to be called on keydown before
    // a change is attempted. Handles Ctrl + Z and Ctrl + Y and Ctrl + Shift Z
    // by updating the state from the history. Return value indicates
    // if the caller should handle keydown.
    if (keyboard.isUndo(e)) {
      this.undoState(selectionData);
      return true;
    } else if (keyboard.isRedo(e)) {
      this.redoState(selectionData);
      return true;
    } else {
      return false;
    }
  };

  override render(): JSX.Element {
    return this.props.renderWith({
      storeState: this.storeState,
      restoreState: this.restoreState,
      undoStoreState: this.undoStoreState,
      undoState: this.undoState,
      redoState: this.redoState,
    });
  }
}

export default DocumentManagerComponent;
